import React, { useEffect, useRef, useState } from 'react';
import './ReservationPromocode.scss';

import { PromoCodeData } from '../../../../../../../../../types/order';
import { createBemClassProp } from '../../../../../../../../../helpers/common';
import { useAppDispatch } from '../../../../../../../../../hooks/reduxHooks';
import { useTranslation } from 'react-i18next';
import { collectAllErrorToString } from '../../../../../../../../../helpers/network';
import { applyPromocodeThunk, removePromocodeThunk } from '../../../../../../../../../store/order/order-thunk';
import AppText from '../../../../../../../../../components/ui/typography/AppText/AppText';

type Props = {
   reservationId: number;
   promoCode?: PromoCodeData;
};

const CheckIcon = () => (
   <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="12" fill="var(--color-primary-green)" fillOpacity="0.1" />
      <path d="M6.26367 12.2607L9.63817 16.0156L17.737 9.13159" stroke="var(--color-primary-green)" strokeWidth="1.43416"
            strokeLinecap="round" strokeLinejoin="round" />
   </svg>
);

const LoaderIcon = () => (
   <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#E6E8EC" strokeWidth="2" />
      <path d="M12 2C6.48 2 2 6.48 2 12" stroke="var(--color-primary-green)" strokeWidth="2" strokeLinecap="round">
         <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
      </path>
   </svg>
);

const ErrorIcon = () => (
   <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
         d="M7 13.5C10.5899 13.5 13.5 10.5899 13.5 7C13.5 3.41015 10.5899 0.5 7 0.5C3.41015 0.5 0.5 3.41015 0.5 7C0.5 10.5899 3.41015 13.5 7 13.5Z"
         stroke="#EB5757"
         strokeLinecap="round"
         strokeLinejoin="round"
      />
      <path d="M7 3.75V7.75" stroke="#EB5757" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.99805 9.80005H7.00805" stroke="#EB5757" strokeLinecap="round" strokeLinejoin="round" />
   </svg>
);

const CloseIcon = () => (
   <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.5 4.5L4.5 13.5" stroke="#777E91" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.5 4.5L13.5 13.5" stroke="#777E91" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
   </svg>
);

const ReservationPromocode: React.FC<Props> = ({ reservationId, promoCode }) => {
   const [isLoading, setIsLoading] = useState(false);
   const [isInputVisible, setIsInputVisible] = useState(false);
   const [value, setValue] = useState('');
   const [error, setError] = useState('');

   const [t] = useTranslation();
   const dispatch = useAppDispatch();
   const isMounted = useRef(true);
   const cn = createBemClassProp('reservation-promocode');

   useEffect(() => {
      return () => {
         isMounted.current = false;
      };
   }, []);

   const onApply = () => {
      if (!value) return;

      setError('');
      setIsLoading(true);
      dispatch(applyPromocodeThunk({ reservationId, promocode: value }))
         .unwrap()
         .then(() => {
            if (isMounted.current) {
               setValue('');
               setIsInputVisible(false);
            }
         })
         .catch((err) => {
            if (isMounted.current) {
               const message = collectAllErrorToString(err);
               setError(message || t('Невірний промокод'));
            }
         })
         .finally(() => {
            if (isMounted.current) {
               setIsLoading(false);
            }
         });
   };

   const onRemove = () => {
      setIsLoading(true);
      dispatch(removePromocodeThunk(reservationId))
         .finally(() => {
            if (isMounted.current) {
               setIsLoading(false);
            }
         });
   };

   if (!promoCode && !isInputVisible) {
      return (
         <div {...cn()}>
            <div {...cn('add-button')} onClick={() => setIsInputVisible(true)}>
               <AppText variant={'B400'}>{t('Додати промокод')}</AppText>
            </div>
         </div>
      );
   }

   const onCancel = () => {
      setIsInputVisible(false);
      setValue('');
      setError('');
   };

   if (isInputVisible) {
      return (
         <div {...cn()}>
            <div {...cn('input-wrapper', error ? 'has-error' : '')}>
               <div {...cn('input-header')}>
                  <label>{t('Ваш промокод')}</label>
                  <div {...cn('cancel')} onClick={onCancel}>
                     <CloseIcon />
                  </div>
               </div>
               <div {...cn('input-field')}>
                  <input
                     type="text"
                     value={value}
                     onChange={(e) => {
                        setValue(e.target.value);
                        if (error) setError('');
                     }}
                     autoFocus
                     disabled={isLoading}
                     onKeyDown={(e) => {
                        if (e.key === 'Enter') onApply();
                     }}
                  />
                  {isLoading ? (
                     <div {...cn('loader')}>
                        <LoaderIcon />
                     </div>
                  ) : (
                     <button
                        type="button"
                        {...cn('apply-btn', !value ? 'disabled' : '')}
                        onClick={onApply}
                        disabled={!value}
                     >
                        {t('Застосувати')}
                     </button>
                  )}
               </div>
               {error && (
                  <div {...cn('error')}>
                     <ErrorIcon />
                     <span>{error}</span>
                  </div>
               )}
            </div>
         </div>
      );
   }

   return (
      <div {...cn()}>
         <div {...cn('applied')}>
            <CheckIcon />
            <span {...cn('applied-label')}>
               <AppText variant={'B400'}>{t('Промокод застосовано')}</AppText>
            </span>
            <span {...cn('applied-code')}>({promoCode?.name})</span>
            <div
               className={`reservation-promocode__remove${isLoading ? ' reservation-promocode__remove--disabled' : ''}`}
               onClick={!isLoading ? onRemove : undefined}
            >
               {isLoading ? <LoaderIcon /> : <CloseIcon />}
            </div>
         </div>
      </div>
   );
};

export default ReservationPromocode;
